import store from "../store/store";
import { all, put, takeEvery } from "@redux-saga/core/effects";
import { bucketNames, getFileUrl } from "../../services/storage";
import { isValidArray, isValidObject } from "../../utils/validators";
import { validDocumentType } from "../../utils/constants";
import { setErrorStatus } from "../status/actions";

export const downloadsActionTypes = {
  GET_PROFILE_PICTURE_DOWNLOAD_URL: "GET_PROFILE_PICTURE_DOWNLOAD_URL",
  GET_DOCUMENT_DOWNLOAD_URL: "GET_DOCUMENT_DOWNLOAD_URL"
};

function* getProfilePicturesUrlWorker(action) {
  try {
    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: true } });

    const { profileType, profileId, uid } = action.payload;
    if (typeof profileType !== "string" || typeof profileId !== "string")
      return;
    if (uid && (typeof uid !== "string" || uid.length === 0)) return;

    const state = store.getState();
    const phoneNumber = state.auth.data.phoneNumber;

    const profileConfig = {
      patient: {
        path: `patients/${phoneNumber}/${profileId}.png`,
        actionType: "SET_PATIENT_PROFILE_PICTURE_URL"
      },
      doctor: {
        path: `doctors/${profileId}.png`,
        actionType: "SET_DOCTOR_PROFILE_PICTURE_URL"
      },
      clinic: {
        path: `clinics/${profileId}.png`,
        actionType: "SET_CLINIC_PROFILE_PICTURE_URL"
      }
    };

    const config = profileConfig[profileType];
    if (!config) {
      console.warn("Invalid profile type for profile picture");
      return;
    }

    const url = yield getFileUrl(
      `gs://${bucketNames.nintoProfilePictures}/${config.path}`
    );
    yield put({ type: config.actionType, payload: { [profileId]: url } });

    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: false } });
  } catch (error) {
    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: false } });

    if (error.message.includes("storage/object-not-found")) {
      yield put({
        type: "SET_PROFILE_PICTURES_URL",
        payload: { [action.payload.profileId]: null }
      });
    } else {
      setErrorStatus(error);
    }
  }
}

function* getDocumentsDownloadUrlWorker(action) {
  try {
    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: true } });

    const { documents, downloads, patients } = store.getState();
    const { data, latest } = documents;
    const { currentProfile } = patients;
    const { documentId } = action.payload;

    if (
      latest !== true ||
      typeof documentId !== "string" ||
      !isValidObject(data)
    ) {
      return yield put({
        type: "SET_DOWNLOADS_LOADING",
        payload: { loading: false }
      });
    }

    if (!data?.[currentProfile]?.[documentId]) {
      return;
    }

    const { documentType, url, notes } = data?.[currentProfile]?.[documentId];

    const docType = validDocumentType(documentType);
    const documentUrl =
      docType === "symptoms"
        ? url?.audio || ""
        : typeof url === "string"
        ? url
        : "";

    let URL;
    if (documentUrl) {
      URL = yield getFileUrl(documentUrl);
    }

    const downloadsData = { ...downloads.data };
    const typeMap = {
      reports: "SET_REPORTS_URL",
      symptoms: "SET_SYMPTOMS_URL",
      prescriptions: "SET_PRESCRIPTIONS_URL",
      insurances: "SET_INSURANCES_URL",
      admissions: "SET_ADMISSIONS_URL",
      receipts: "SET_RECEIPTS_URL",
      vitals: "SET_VITALS_URL",
      vaccines: "SET_VACCINES_URL",
      consultations: "SET_CONSULTATIONS_URL",
      invoices: "SET_INVOICES_URL",
      others: "SET_OTHERS_URL"
    };

    const currentData = downloadsData[docType] || {};
    if (
      !currentData ||
      !currentData.hasOwnProperty(documentId) ||
      typeof currentData[documentId] !== "string"
    ) {
      const newEntry = { downloadUrl: URL, notes: null };

      // Build notes object for document types that can have notes
      if (
        ["reports", "vitals", "vaccines", "consultations", "others"].includes(
          docType
        ) &&
        isValidObject(notes)
      ) {
        const newNotesObj = {};
        for (const doctorId in notes) {
          const defaultPdfName = url.split("/").pop();
          const convertedUrlPath = url.replace(
            defaultPdfName,
            `${doctorId}.pdf`
          );
          newNotesObj[doctorId] = yield getFileUrl(convertedUrlPath);
        }
        newEntry.notes = isValidObject(newNotesObj) ? newNotesObj : null;
      }

      // Build images array for symptoms
      if (docType === "symptoms" && isValidArray(url?.images)) {
        const images = [];
        for (const img of url.images) {
          images.push(yield getFileUrl(img));
        }
        newEntry.images = images;
      }

      currentData[documentId] = newEntry;
      yield put({
        type: typeMap[docType],
        payload: { [`${docType}Url`]: currentData }
      });
    }

    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: false } });
  } catch (error) {
    yield put({ type: "SET_DOWNLOADS_LOADING", payload: { loading: false } });
    setErrorStatus(error);
  }
}

export default function* downloadsWatcher() {
  yield all([
    takeEvery("GET_PROFILE_PICTURE_DOWNLOAD_URL", getProfilePicturesUrlWorker),
    takeEvery("GET_DOCUMENT_DOWNLOAD_URL", getDocumentsDownloadUrlWorker)
  ]);
}
